import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useEffect, useState } from "react";
import { Button } from "@/shared/components/Button";
import { Input } from "@/shared/components/Input";
import { Modal } from "@/shared/components/Modal";
import { CampoSelect, CampoTexto } from "@/modules/visitantes/components/Campos";
import { useRegistrarContato } from "@/modules/visitantes/hooks/useVisitantes";
import { CANAIS_CONTATO, hojeSaoPaulo, type CanalContato } from "@/modules/visitantes/utils/regras";

interface Props {
  visitante: { id: string; nome: string } | null;
  /** Canal sugerido (ex.: veio de "WhatsApp" → "whatsapp"). */
  canalInicial?: CanalContato;
  onClose: () => void;
  onRegistrado: () => void;
}

/** Registra um contato feito com o visitante (ministério responsável e líderes de outros ministérios). */
export function ModalRegistrarContato({
  visitante,
  canalInicial = "whatsapp",
  onClose,
  onRegistrado,
}: Props) {
  const registrar = useRegistrarContato();
  const [data, setData] = useState(hojeSaoPaulo());
  const [canal, setCanal] = useState<CanalContato>(canalInicial);
  const [obs, setObs] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (visitante) {
      setData(hojeSaoPaulo());
      setCanal(canalInicial);
      setObs("");
      setErro(null);
    }
  }, [visitante, canalInicial]);

  async function confirmar() {
    if (!visitante) return;
    setErro(null);
    if (!data || data > hojeSaoPaulo()) {
      setErro("Data do contato inválida.");
      return;
    }
    try {
      await registrar.mutateAsync({ id: visitante.id, data, canal, observacao: obs });
      onRegistrado();
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível registrar o contato.");
    }
  }

  return (
    <Modal
      titulo="Registrar contato"
      aberto={!!visitante}
      onClose={onClose}
      alterado={obs.trim() !== "" || canal !== canalInicial || data !== hojeSaoPaulo()}
    >
      <p className="mb-4 text-sm text-muted">
        Conversou com {visitante?.nome}? Registre para que os outros líderes saibam que a pessoa já
        foi acompanhada.
      </p>
      <div className="flex flex-col gap-4">
        <Input
          label="Data do contato"
          name="dataContato"
          type="date"
          max={hojeSaoPaulo()}
          value={data}
          onChange={(e) => setData(e.target.value)}
        />
        <CampoSelect
          label="Como foi?"
          name="canal"
          value={canal}
          onChange={(e) => setCanal(e.target.value as CanalContato)}
        >
          {CANAIS_CONTATO.map((c) => (
            <option key={c.chave} value={c.chave}>
              {c.rotulo}
            </option>
          ))}
        </CampoSelect>
        <CampoTexto
          label="Observação (opcional)"
          name="observacaoContato"
          maxLength={500}
          value={obs}
          onChange={(e) => setObs(e.target.value)}
        />
      </div>
      {erro && (
        <p role="alert" className="mt-3 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {erro}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <BotaoVoltar onClick={onClose} className="min-h-11" />
        <Button
          type="button"
          onClick={confirmar}
          carregando={registrar.isPending}
          className="min-h-11"
        >
          Registrar contato
        </Button>
      </div>
    </Modal>
  );
}
